import { Injectable, Output, EventEmitter } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Helpers } from '../helpers/helpers';
import { loadCdnScript } from 'src/app/helpers/cdn-script-loader/cdn-script-loader';
import {
  IPortalNavigation,
  IUserPortalFeatures,
  IPortalConfig,
  Tools,
} from 'src/app/models';
import { lastValueFrom } from 'rxjs';
import { ISite, Site } from 'src/app/models/site/site';
import { ICountry } from 'src/app/models/community/leaderboard';
import { UserFavoritePortal } from '../models/user/userFavoritePortal';
import { Dashboard } from '../models/dashboard/dashboard';
import {
  MilestoneActivity,
  StartupTimeline,
} from '../models/dashboard/startup-timeline';
import {
  ConsolidateSiteView,
  Milestone,
} from '../models/dashboard/consolidate-siteview';
import { EmailTemplate } from '../models/emailTemplate';
import {
  MilestoneActivityType,
  MilestonePhase,
  MilestoneActivityAssignmentType,
  MilestoneActivityAssignmentSelection,
  Folder,
  PublishMilestoneActivity,
  SiteMilestoneActivityStatus,
} from '../models/milestone';
import { CoreSiteMapping } from '../models/core/coresitemapping';
import { PortalSiteInfo } from '../models/site/portalsiteinfo';
import { SiteMilestoneActivity } from '../models/milestone/siteMilestoneActivity';
import { SurveyQuestion } from '../models/workspace/survey-question';
import { WorkspaceFilter, WorkspaceMetric } from '../models/workspace/workspace';
import { SiteStartup, SiteStartupTimelineMetrics, StartupEmailTask } from '../models/site/sitestartup';
import { StartupDeclineReason } from '../models/site/startupdeclinereason';
import { SiteStartupStatus } from '../models/site/startupstatus';
import { SiteActivationForecastMetrics } from '../models/site/siteactivationforecastmetrics';
import { AIFeedbackMetric, AIMessage, AIPredefinedQuestion, AIQuestionCountMetric, RootQuestionMetric } from '../models/AI/ai-message';
import { SearchQueryRequest } from '../models/AI/search';
declare global {
  interface Window {
    EXPLORE_APPS: any;
  }
}

@Injectable({
  providedIn: 'root',
})

export class CoreService {
  private lookups = new Map<string, any>();
  private Core_URL =
    environment.triportCoreService.url +
    environment.triportCoreService.apiVersion;

  private formatLocalDateTime(date: Date): string {
    const pad = (value: number) => value.toString().padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  allExploreAppsLookupKey = 'ExploreApps';
  allUserPortalsLookupKey = 'AllUserPortals';
  allUserPlatformsLookupKey = 'AllUserPlatforms';
  UserPortalFeatureNavigationLookupKey = 'PortalUserFeatureNavigation';
  UserPortalAllFeaturesLookupKey = 'PortalUserAllFeatures';
  UserUserToolNavigationLookupKey = 'UserToolNavigation';
  PortalInfoLookupKey = 'PortalInfo';
  PortalFeatureContentLookupKey = 'PortalFeature';
  UserPortalInfoLookupKey = 'UserPortalInfo';
  portalSitesLookupKey = 'PortalSites';
  portalSiteUsersLookupKey = 'portalSiteUsers';
  portalPIUsersLookupKey = 'portalPIUsers';
  portalCountryLookupKey = 'PortalCountries';
  portalModeratorsLookupKey = 'portalModerators';
  startupPortalSitesLookupKey = 'StartupPortalSites';
  startupPortalSurveyQuestionsLookup = 'StartupPortalSurveyQuestions';
  startupPortalDeclineReasonsLookup = 'StartupPortalDeclineReasons';
  startupPortalStatusesLookup = 'startupPortalStatuses';
  MedPointAdminFeatureNavigationLookupKey = "MedPointAdminFeatureNavigation";
  UserAdminFeatureNavigationLookupKey = "UserAdminFeatureNavigation";
  AIPredefinedQuestionsLookupKey = 'AIPredefinedQuestions';

  constructor(private http: HttpClient) {}

  getExploreAppsList(): Promise<any> {
    return new Promise(async (resolve, reject) => {

      // 1️⃣ Return from cache
      if (this.lookups.has(this.allExploreAppsLookupKey)) {
        resolve(this.lookups.get(this.allExploreAppsLookupKey));
        return;
      }

      const scriptUrl =
        `${environment.cdnEndPoint}/triport/others/ExploreApps.js`;

      try {
        // 2️⃣ Load CDN script dynamically
        await loadCdnScript(scriptUrl);

        if (!window.EXPLORE_APPS) {
          reject('EXPLORE_APPS not available after script load');
          return;
        }

        // 3️⃣ Cache and resolve
        this.lookups.set(this.allExploreAppsLookupKey, window.EXPLORE_APPS);
        resolve(window.EXPLORE_APPS);

      } catch (err) {
        reject(err);
      }
    });
  }
  clearUserPortalInfoLookupKey(portalName:string){
   this.lookups.delete(this.UserPortalInfoLookupKey + '_' + portalName); 
  }
  getUserPortals() {
    return new Promise<Array<IPortalConfig>>((resolve, reject) => {
      if (this.lookups.has(this.allUserPortalsLookupKey)) {
        resolve(this.lookups.get(this.allUserPortalsLookupKey));
      } else {
        this.http
          .get<Array<IPortalConfig>>(
            this.Core_URL + `User/UserPortals/${Helpers.getPlatformId()}`
          )
          .toPromise()
          .then(
            (portals?: Array<IPortalConfig>) => {
              this.lookups.set(this.allUserPortalsLookupKey, portals);
              resolve(portals!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getUserPlatforms() {
    return new Promise<any>((resolve, reject) => {
      if (this.lookups.has(this.allUserPlatformsLookupKey)) {
        resolve(this.lookups.get(this.allUserPlatformsLookupKey));
      } else {
        this.http
          .get<any>(
            this.Core_URL + `User/UserPlatforms/${Helpers.getPlatformId()}`
          )
          .toPromise()
          .then(
            (platforms) => {
              this.lookups.set(this.allUserPlatformsLookupKey, platforms);
              resolve(platforms);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getUserFeatureNavigation(
    sponsorName: string,
    portalId: number
  ): Promise<IPortalNavigation[]> {
    return new Promise<IPortalNavigation[]>((resolve, reject) => {
      if (
        this.lookups.has(
          this.UserPortalFeatureNavigationLookupKey + '_' + portalId
        )
      ) {
        resolve(
          this.lookups.get(
            this.UserPortalFeatureNavigationLookupKey + '_' + portalId
          )
        );
      } else {
        this.http
          .get<IPortalNavigation[]>(
            this.Core_URL +
              `User/UserFeatureNavigation/${sponsorName}/${portalId}`
          )
          .toPromise()
          .then(
            (navigations) => {
              this.lookups.set(
                this.UserPortalFeatureNavigationLookupKey + '_' + portalId,
                navigations
              );
              resolve(navigations!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getAllUserFeatures(
    sponsorName: string,
    portalId: number
  ): Promise<IUserPortalFeatures[]> {
    return new Promise<IUserPortalFeatures[]>((resolve, reject) => {
      if (
        this.lookups.has(this.UserPortalAllFeaturesLookupKey + '_' + portalId)
      ) {
        resolve(
          this.lookups.get(this.UserPortalAllFeaturesLookupKey + '_' + portalId)
        );
      } else {
        this.http
          .get<IUserPortalFeatures[]>(
            this.Core_URL + `User/UserFeatures/${sponsorName}/${portalId}`
          )
          .toPromise()
          .then(
            (allfeatures) => {
              this.lookups.set(
                this.UserPortalAllFeaturesLookupKey + '_' + portalId,
                allfeatures
              );
              resolve(allfeatures!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getUserToolNavigation(
    sponsorName: string = '',
    portalId: number = 0
  ): Promise<Tools[]> {
    return new Promise<Tools[]>((resolve, reject) => {
      if (
        this.lookups.has(this.UserUserToolNavigationLookupKey + '_' + portalId)
      ) {
        resolve(
          this.lookups.get(
            this.UserUserToolNavigationLookupKey + '_' + portalId
          )
        );
      } else {
        let url =
          portalId == 0
            ? this.Core_URL + 'User/UserToolNavigation'
            : this.Core_URL +
              `User/UserToolNavigation/${sponsorName}/${portalId}`;
        this.http
          .get<Tools[]>(url)
          .toPromise()
          .then(
            (tools) => {
              this.lookups.set(
                this.UserUserToolNavigationLookupKey + '_' + portalId,
                tools
              );
              resolve(tools!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getPortalContent(sponsorName: string, portalId: number, contentPage: string) {
    return new Promise<any>((resolve, reject) => {
      if (
        this.lookups.has(
          this.PortalFeatureContentLookupKey +
            '_' +
            portalId +
            '_' +
            contentPage
        )
      ) {
        resolve(
          this.lookups.get(
            this.PortalFeatureContentLookupKey +
              '_' +
              portalId +
              '_' +
              contentPage
          )
        );
      } else {
        this.http
          .get<any>(
            this.Core_URL +
              `Portal/PortalContent/${sponsorName}/${portalId}/` +
              contentPage
          )
          .toPromise()
          .then(
            (potalContent) => {
              this.lookups.set(
                this.PortalFeatureContentLookupKey +
                  '_' +
                  portalId +
                  '_' +
                  contentPage,
                potalContent
              );
              resolve(potalContent!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getUserPortalInfo(
    sponsorName: string,
    portalName: string
  ): Promise<IPortalConfig> {
    return new Promise<IPortalConfig>((resolve, reject) => {
      if (this.lookups.has(this.UserPortalInfoLookupKey + '_' + portalName)) {
        resolve(
          this.lookups.get(this.UserPortalInfoLookupKey + '_' + portalName)
        );
      } else {
        this.http
          .get<IPortalConfig>(
            this.Core_URL + `User/UserPortalInfo/${sponsorName}/${portalName}`
          )
          .toPromise()
          .then(
            (userPotalConfig?: IPortalConfig) => {
              this.lookups.set(
                this.UserPortalInfoLookupKey + '_' + portalName,
                userPotalConfig
              );
              resolve(userPotalConfig!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  generateWebChatToken(PortalID?: number): Promise<any> {
    let url = `${this.Core_URL}User/GenerateWebChatToken/${PortalID}`;
  
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(url)
      ).then(
        (config?: any) => {
          resolve(config!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }  

  getPortalSites(sponsorName: string, portalId: number) {
    return new Promise<Array<ISite>>((resolve, reject) => {
      if (this.lookups.has(this.portalSitesLookupKey + '_' + portalId)) {
        resolve(this.lookups.get(this.portalSitesLookupKey + '_' + portalId));
      } else {
        lastValueFrom(
          this.http.get<Array<ISite>>(
            this.Core_URL + `Portal/Sites/${sponsorName}/${portalId}`
          )
        ).then(
          (sites?: Array<ISite>) => {
            this.lookups.set(this.portalSitesLookupKey + '_' + portalId, sites);
            resolve(sites!);
          },
          (err) => {
            reject(err);
          }
        );
      }
    });
  }
  getProjectTeamSites(sponsorName: string, portalId: number, platformId: number) {
    return new Promise<Array<ISite>>((resolve, reject) => {
      if (this.lookups.has(this.portalSitesLookupKey + '_' + portalId)) {
        resolve(this.lookups.get(this.portalSitesLookupKey + '_' + portalId));
      } else {
        lastValueFrom(
          this.http.get<Array<ISite>>(
            this.Core_URL + `Portal/ProjectTeamSites/${sponsorName}/${portalId}/${platformId}`
          )
        ).then(
          (sites?: Array<ISite>) => {
            this.lookups.set(this.portalSitesLookupKey + '_' + portalId, sites);
            resolve(sites!);
          },
          (err) => {
            reject(err);
          }
        );
      }
    });
  }
  getStartupPortalSites(sponsorName: string, portalId: number) {
    return new Promise<Array<ISite>>((resolve, reject) => {
      if (this.lookups.has(this.startupPortalSitesLookupKey + '_' + portalId)) {
        resolve(
          this.lookups.get(this.startupPortalSitesLookupKey + '_' + portalId)
        );
      } else {
        lastValueFrom(
          this.http.get<Array<ISite>>(
            this.Core_URL + `Portal/Startup/Sites/${sponsorName}/${portalId}`
          )
        ).then(
          (sites?: Array<ISite>) => {
            this.lookups.set(
              this.startupPortalSitesLookupKey + '_' + portalId,
              sites
            );
            resolve(sites!);
          },
          (err) => {
            reject(err);
          }
        );
      }
    });
  }
  manageUserFavoritePortal(
    userFavoritePortal: UserFavoritePortal
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.post<any>(
          this.Core_URL + 'User/UserFavoritePortal',
          userFavoritePortal
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  getSiteUsers(
    sponsorName: string,
    portalId: number,
    siteId: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (
        this.lookups.has(
          this.portalSiteUsersLookupKey + '_' + portalId + '_' + siteId
        )
      ) {
        resolve(
          this.lookups.get(
            this.portalSiteUsersLookupKey + '_' + portalId + '_' + siteId
          )
        );
      } else {
        this.http
          .get<any>(
            this.Core_URL + `User/SiteUser/${sponsorName}/${portalId}/${siteId}`
          )
          .toPromise()
          .then(
            (siteusers?: any) => {
              this.lookups.set(
                this.portalSiteUsersLookupKey + '_' + portalId + '_' + siteId,
                siteusers
              );
              resolve(siteusers!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getSitePIUsers(
    sponsorName: string,
    portalId: number,
    siteId: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (
        this.lookups.has(
          this.portalPIUsersLookupKey + '_' + portalId + '_' + siteId
        )
      ) {
        resolve(
          this.lookups.get(
            this.portalPIUsersLookupKey + '_' + portalId + '_' + siteId
          )
        );
      } else {
        this.http
          .get<any>(
            this.Core_URL + `User/PIUser/${sponsorName}/${portalId}/${siteId}`
          )
          .toPromise()
          .then(
            (siteusers?: any) => {
              this.lookups.set(
                this.portalPIUsersLookupKey + '_' + portalId + '_' + siteId,
                siteusers
              );
              resolve(siteusers!);
            },
            (err) => {
              reject(err);
            }
          );
      }
    });
  }

  getCountry(sponsorName: string, portalId: number) {
    return new Promise<Array<ICountry>>((resolve, reject) => {
      if (this.lookups.has(this.portalCountryLookupKey + '_' + portalId)) {
        resolve(this.lookups.get(this.portalCountryLookupKey + '_' + portalId));
      } else {
        lastValueFrom(
          this.http.get<Array<ICountry>>(
            this.Core_URL + `Portal/Country/${sponsorName}/${portalId}`
          )
        ).then(
          (country?: Array<ICountry>) => {
            this.lookups.set(
              this.portalCountryLookupKey + '_' + portalId,
              country
            );
            resolve(country!);
          },
          (err) => {
            reject(err);
          }
        );
      }
    });
  }
  getAllCountries(sponsorName: string, portalId: number) {
    return new Promise<Array<ICountry>>((resolve, reject) => {
      if (this.lookups.has(this.portalCountryLookupKey + '_' + portalId)) {
        resolve(this.lookups.get(this.portalCountryLookupKey + '_' + portalId));
      } else {
        lastValueFrom(
          this.http.get<Array<ICountry>>(
            this.Core_URL + `Portal/Countries/${sponsorName}/${portalId}`
          )
        ).then(
          (country?: Array<ICountry>) => {
            this.lookups.set(
              this.portalCountryLookupKey + '_' + portalId,
              country
            );
            resolve(country!);
          },
          (err) => {
            reject(err);
          }
        );
      }
    });
  }

  getModerators(sponsorName: string, portalId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.lookups.has(this.portalModeratorsLookupKey + '_' + portalId)) {
        resolve(
          this.lookups.get(this.portalModeratorsLookupKey + '_' + portalId)
        );
      } else {
        lastValueFrom(
          this.http.get<any>(
            this.Core_URL + `User/Moderator/${sponsorName}/${portalId}`
          )
        ).then(
          (siteusers?: any) => {
            this.lookups.set(
              this.portalModeratorsLookupKey + '_' + portalId,
              siteusers
            );
            resolve(siteusers!);
          },
          (err) => {
            reject(err);
          }
        );
      }
    });
  }

  getDiscussionBoardStatus(sponsorName: string, portalId: number) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.get<any>(
          `${this.Core_URL}Portal/DiscussionBoardStatus/${sponsorName}/${portalId}`
        )
      ).then(
        (forms: any) => {
          resolve(forms);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  getUserDashboard(sponsorName: string, portalId: number) {
    return new Promise<Dashboard>((resolve, reject) => {
      lastValueFrom(
        this.http.get<Dashboard>(
          this.Core_URL + 'User/Dashboard/' + sponsorName + '/' + portalId
        )
      ).then(
        (dashboard?: Dashboard) => {
          resolve(dashboard!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getSiteCRAUserExisits(sponsorName: string, portalId: number) {
    return new Promise<boolean>((resolve, reject) => {
      lastValueFrom(
        this.http.get<boolean>(
          this.Core_URL +
            'User/SiteCRAUserExisits/' +
            sponsorName +
            '/' +
            portalId
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  isUserPrimaryCRA(sponsorName: string, portalId: number, siteId: number) {
    return new Promise<boolean>((resolve, reject) => {
      lastValueFrom(
        this.http.get<boolean>(
          this.Core_URL +
          'User/IsUserPrimaryCra/' +
          sponsorName +
          '/' +
          portalId +
          '/' +
          siteId
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getEnableSiteFilter(sponsorName: string, portalId: number) {
    return new Promise<boolean>((resolve, reject) => {
      lastValueFrom(
        this.http.get<boolean>(
          this.Core_URL +
            'User/EnableSiteFilter/' +
            sponsorName +
            '/' +
            portalId
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getStartupTimelinesForSite(
    sponsorName: string,
    portalId: number,
    siteId: number,
    milestoneId: number | null
  ) {
    var milestone = '';
    if (milestoneId != null) {
      milestone = '/' + milestoneId;
    }
    return new Promise<StartupTimeline>((resolve, reject) => {
      lastValueFrom(
        this.http.get<StartupTimeline>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalId +
            '/' +
            siteId +
            '/StartupTimelines' +
            milestone
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  updateMilestoneActivityStatus(
    sponsorName: string,
    siteId: number,
    milestoneActivity: MilestoneActivity
  ) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.put<any>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            siteId +
            '/StartupTimelines',
          milestoneActivity
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  expireMilestone(sponsorName: string, portalId: number, milestoneId: number) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.delete<any>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalId +
            '/Builder/Milestones/' +
            milestoneId
        )
      ).then(
        (logs: any) => {
          resolve(logs);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getConsolidatedSiteView(
    sponsorName: string,
    portalId: number,
    milestonePhaseId: number
  ) {
    let path = '';
    if (milestonePhaseId != 0) {
      path = '?milestonePhaseId=' + milestonePhaseId;
    }
    return new Promise<ConsolidateSiteView>((resolve, reject) => {
      lastValueFrom(
        this.http.get<ConsolidateSiteView>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalId +
            '/Milestones' +
            path
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getMilestonePhases(sponsorName: string, portalId: number) {
    return new Promise<MilestonePhase[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<MilestonePhase[]>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalId +
            '/MilestonePhases'
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  // api/v1/User/RoleTypeName/{sponsorName}/{portalId}
  getRoleTypeName(sponsorName: string, portalId: number) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.get<any>(
          this.Core_URL + 'User/RoleTypeName/' + sponsorName + '/' + portalId
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getMilestoneAssignments(sponsorName: string, portalId: number) {
    return new Promise<MilestoneActivityAssignmentType[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<MilestoneActivityAssignmentType[]>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalId +
            '/MilestoneAssignments'
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getStartupTimelinerBuilder(
    sponsorName: string,
    portalId: number,
    milestonePhaseId: number
  ) {
    let path = '';
    if (milestonePhaseId != 0) {
      path = '?milestonePhaseId=' + milestonePhaseId;
    }
    return new Promise<Milestone[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<Milestone[]>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalId +
            '/Builder/Milestones' +
            path
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getMilestonesByPortal(sponsorName: string, portalId: number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.Core_URL + `Portal/${sponsorName}/${portalId}/AllMilestones`))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }
  getAllSites(sponsorName: string, portalId: number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.Core_URL + `Portal/AllSites/${sponsorName}/${portalId}`))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }
  updateMilestoneOrder(
    sponsorName: string,
    portalid: number,
    milestones: Milestone[]
  ) {
    let body = JSON.stringify(milestones);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.post(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalid +
            '/Builder/Milestones',
          body
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  addMilestone(sponsorName: string, portalid: number, milestone: any) {
    let body = JSON.stringify(milestone);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.post(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalid +
            '/Milestones',
          body
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  editMilestone(sponsorName: string, portalid: number, milestone: any) {
    let body = JSON.stringify(milestone);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.patch(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalid +
            '/Milestones',
          body
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getEmailTemplates(sponsorName: string, portalId: number) {
    return new Promise<EmailTemplate[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<EmailTemplate[]>(
          this.Core_URL +
            'Portal/' +
            sponsorName +
            '/' +
            portalId +
            '/EmailTemplates'
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  GetIsAssetAssignedInMultiplePortals(sponsorName: string, AssetId: number) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.get<any>(
          this.Core_URL +
            'Asset/IsAssetAssignedInMultiplePortals/' +
            sponsorName +
            '/' +
            AssetId
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getMilestoneActivityTypes(sponsorName: string) {
    return new Promise<MilestoneActivityType[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<MilestoneActivityType[]>(
          this.Core_URL + 'milestones/ActivityTypes/' + sponsorName
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getMilestoneAssignmentTypes(sponsorName: string) {
    return new Promise<MilestoneActivityAssignmentType[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<MilestoneActivityAssignmentType[]>(
          this.Core_URL + 'milestones/ActivityAssignmentTypes/' + sponsorName
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getMilestoneActivityAssignmentSelections(sponsorName: string) {
    return new Promise<MilestoneActivityAssignmentSelection[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<MilestoneActivityAssignmentSelection[]>(
          this.Core_URL + 'milestones/ActivityAssignmentSelections/' + sponsorName
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getSiteVerifyNotificationByUser(sponsorName: string, portalId: number) {
    return new Promise<ISite>((resolve, reject) => {
      lastValueFrom(this.http.get<ISite>(this.Core_URL + 'User/siteverifynotification/' + sponsorName + "/" + portalId))
        .then((response?: ISite) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
        });

    });
  }
  getSiteVerificationInfoById(sponsorName: string, portalId: number, siteId:number) {
    return new Promise<PortalSiteInfo>((resolve, reject) => {
      lastValueFrom(this.http.get<PortalSiteInfo>(this.Core_URL + 'portal/' + sponsorName + "/" + portalId + "/siteverification/" + siteId))
        .then((response?: PortalSiteInfo) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
        });

    });
  }
  updateSite(sponsorName: string, portalSiteInfo: PortalSiteInfo){
    return new Promise<boolean>((resolve, reject) => {
       lastValueFrom(this.http.put<any>(this.Core_URL + 'Portal/' + sponsorName + '/site',portalSiteInfo ))
           .then((response?: any) => {
             resolve(response!);
           },
             (err) => {
               reject(err);
             });
      
     });
   }
   manageCoreSiteMapping(sponsorName: string, coresiteMapping: CoreSiteMapping){
    return new Promise<boolean>((resolve, reject) => {
       lastValueFrom(this.http.post<any>(this.Core_URL + 'Portal/' + sponsorName + '/coresitemapping',coresiteMapping ))
           .then((response?: any) => {
             resolve(response!);
           },
             (err) => {
               reject(err);
             });
      
     });
    }
    addMilestoneActivity(sponsorName: string, portalid: number, milestone: any) {
      let body = JSON.stringify(milestone);
      return new Promise<any>((resolve, reject) => {
        lastValueFrom(
          this.http.post(
            this.Core_URL +
              'milestones/' +
              milestone.MilestoneId +
              '/milestoneactivities/' +
              sponsorName +
              '/' +
              portalid,              
            body
          )
        ).then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
      });
    }
    
  GetDocumentUploadURL(sponsorName: string, portalId: number, fileName: string | undefined) {
    let postData = { FileName: fileName, FolderPath : ''};
    let body =JSON.stringify(postData);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom( this.http.post<any>(this.Core_URL + `Portal/UploadUrl/${sponsorName}/${portalId}/`, body)
        ).then((response?: any) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
          });
    });
  }
  addFileToDB(sponsorName: string, portalid: number, fileName: string | undefined) {
    let postData = { FileName: fileName, FolderPath : ''};
    let body =JSON.stringify(postData);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.post(
          this.Core_URL +
            'portal/addfileToDB/' +
            sponsorName +
            '/' +
            portalid,              
          body
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getPortalFolders(sponsorName: string, portalId: number) {
    return new Promise<Folder[]>((resolve, reject) => {
      lastValueFrom(this.http.get<Folder[]>(this.Core_URL + 'Portal/' + sponsorName + "/" + portalId + "/folders"))
        .then((response?: Folder[]) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
        });

    });
  }
  archiveMilestoneActivity(sponsorName: string, milestoneActivity: any) {
    let body = JSON.stringify(milestoneActivity);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.patch(
          this.Core_URL +
            'milestones/' +
            'milestoneactivities/' +
            sponsorName,            
          body
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  removeMilestoneActivity(sponsorName: string, milestoneActivityId: number) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.delete(
          this.Core_URL +
            'milestones/' +
            'milestoneactivities/' +
             milestoneActivityId + '/' +
            sponsorName          
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  editMilestoneActivity(sponsorName: string, portalid: number, milestone: any) {
    let body = JSON.stringify(milestone);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.patch(
          this.Core_URL +
            'milestones/' +
            milestone.MilestoneId +
            '/milestoneactivities/' +
            sponsorName +
            '/' +
            portalid,              
          body
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getMilestoneActivitiesForPublish(sponsorName: string, portalId: number) {
    return new Promise<MilestoneActivity[]>((resolve, reject) => {
      lastValueFrom(this.http.get<MilestoneActivity[]>(this.Core_URL + 'Milestones/publish/' + sponsorName + "/" + portalId))
        .then((response?: MilestoneActivity[]) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
        });

    });
  }
  publishMilestoneActivity(sponsorName: string, portalId: number, publishMilestoneActivity: PublishMilestoneActivity) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.Core_URL + 'Milestones/publish/' + sponsorName + "/" + portalId,publishMilestoneActivity))
        .then((response?: any) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
        });

    });
  }
  getSiteMilestoneActivityStatuses(sponsorName: string) {
    return new Promise<SiteMilestoneActivityStatus[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<SiteMilestoneActivityStatus[]>(
          this.Core_URL + 'milestones/SiteMilestoneActivityStatus/' + sponsorName
        )
      ).then(
        (response?: any) => {
          resolve(response!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  updateSiteMilestoneTimelineActivity(sponsorName: string, siteMilestoneActivity: SiteMilestoneActivity, portalId: number){
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.patch<any>(this.Core_URL + 'Milestones/timeline/' + sponsorName + '/' + portalId, siteMilestoneActivity))
        .then((response?: any) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
        });
    });
  }
  getSurveyQuestions(sponsorName: string, portalId: number, surveyId :number) {
    return new Promise<Array<SurveyQuestion>>((resolve, reject) => {
      if (this.lookups.has(this.startupPortalSurveyQuestionsLookup + '_' + surveyId)) {
        resolve(this.lookups.get(this.startupPortalSurveyQuestionsLookup + '_' + surveyId));
      } else {
        lastValueFrom(
          this.http.get<Array<SurveyQuestion>>(
            this.Core_URL + `Portal/surveyquestions/${sponsorName}/${portalId}/${surveyId}`
          )
        ).then(
          (questions?: Array<SurveyQuestion>) => {
            this.lookups.set(
              this.startupPortalSurveyQuestionsLookup + '_' + portalId,
              questions
            );
            resolve(questions!);
          },
          (err) => {
            reject(err);
          }
        );
      }
    });
  }
  getWorkspaceMetrics(sponsorName: string, portalId: number,viewType:number) {
    return new Promise<WorkspaceMetric[]>((resolve, reject) => {
      lastValueFrom(
        this.http.get<WorkspaceMetric[]>(
          this.Core_URL + `Milestones/${sponsorName}/${portalId}` + `/WorkspaceMetrics/${viewType}`
        )
      ).then(
        (metrics?: WorkspaceMetric[]) => {
          resolve(metrics!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getWorkspace(sponsorName: string, portalId: number,viweType:number, workspaceFilter: WorkspaceFilter) {
    return new Promise<any[]>((resolve, reject) => {
      lastValueFrom(
        this.http.post<any[]>(
          this.Core_URL + `Milestones/${sponsorName}/${portalId}` + `/Workspace/${viweType}`,workspaceFilter
        )
      ).then(
        (workspce?: any[]) => {
          resolve(workspce!);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  manageSiteStartup(sponsorName: string, siteStartups: SiteStartup[]){
    return new Promise<boolean>((resolve, reject) => {
      lastValueFrom(this.http.post<boolean>(this.Core_URL + 'Milestones/' + sponsorName + '/sitestartup', siteStartups))
        .then((response?: boolean) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
        });
    });
    }
    getSurveys(sponsorName: string, portalId: number) {
        return new Promise<any>((resolve, reject) => {
            lastValueFrom(this.http.get<any>(this.Core_URL + 'Milestones/' + sponsorName + '/' + portalId + "/Surveys"))
                .then((response?: any) => {
                    resolve(response!);
                },
                    (err) => {
                        reject(err);
                    });
        });
    }
    getStatupDeclineReasons(sponsorName: string) {
      return new Promise<Array<StartupDeclineReason>>((resolve, reject) => {
        if (this.lookups.has(this.startupPortalDeclineReasonsLookup + '_' + sponsorName)) {
          resolve(this.lookups.get(this.startupPortalDeclineReasonsLookup + '_' + sponsorName));
        } else {
          lastValueFrom(
            this.http.get<Array<StartupDeclineReason>>(
              this.Core_URL + `Portal/${sponsorName}` + `/startupdeclinereasons`
            )
          ).then(
            (startupDeclineReasons?: Array<StartupDeclineReason>) => {
              this.lookups.set(
                this.startupPortalDeclineReasonsLookup + '_' + sponsorName,
                startupDeclineReasons
              );
              resolve(startupDeclineReasons!);
            },
            (err) => {
              reject(err);
            }
          );
        }
      });
     
    }
    insertEmailTaskProcessForStartup(sponsorName: string, startupEmailTask: StartupEmailTask){
      return new Promise<any>((resolve, reject) => {
        lastValueFrom(this.http.post<any>(this.Core_URL + 'Portal/' + sponsorName + '/startupemailtask', startupEmailTask))
          .then((response?: any) => {
            resolve(response!);
          },
            (err) => {
              reject(err);
          });
      });
    }
    getSiteStatupStatuses(sponsorName: string) {
      return new Promise<Array<SiteStartupStatus>>((resolve, reject) => {
        if (this.lookups.has(this.startupPortalStatusesLookup + '_' + sponsorName)) {
          resolve(this.lookups.get(this.startupPortalStatusesLookup + '_' + sponsorName));
        } else {
          lastValueFrom(
            this.http.get<Array<SiteStartupStatus>>(
              this.Core_URL + `Portal/${sponsorName}` + `/sitestarupstatuses`
            )
          ).then(
            (siteStartupStatuses?: Array<SiteStartupStatus>) => {
              this.lookups.set(
                this.startupPortalStatusesLookup + '_' + sponsorName,
                siteStartupStatuses
              );
              resolve(siteStartupStatuses!);
            },
            (err) => {
              reject(err);
            }
          );
        }
      });
     
    }
    updateSiteStartupStatus(sponsorName: string, site: Site){
      return new Promise<any>((resolve, reject) => {
        lastValueFrom(this.http.put<any>(this.Core_URL + 'Portal/' + sponsorName + '/sitestartupstatus', site))
          .then((response?: any) => {
            resolve(response!);
          },
            (err) => {
              reject(err);
          });
      });
    }
  
GetUserByRoleType(sponsorName: string,portalId : number,siteId:number,roleTypeName:any) {
  return new Promise<any[]>((resolve, reject) => {
    lastValueFrom(this.http.post<any[]>(this.Core_URL + "User/GetUserByRoleType/" + sponsorName +"/" +portalId +"/" + siteId , JSON.stringify(roleTypeName)))
      .then(
        (response: any[]) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
  });
    }
    getSiteStartupTimelineMetrics(sponsorName: string, siteId: number) {
        return new Promise<SiteStartupTimelineMetrics>((resolve, reject) => {
            lastValueFrom(this.http.get<SiteStartupTimelineMetrics>(this.Core_URL + 'Portal/' + sponsorName + '/' + siteId + '/StartupTimelineMetrics'))
                .then((response?: SiteStartupTimelineMetrics) => {
                    resolve(response!);
                },
                    (err) => {
                        reject(err);
                    });
        });
    }
    getSiteActivationForecastMetrics(sponsorName: string, portalId: number, countries: number[]) {
        return new Promise<SiteActivationForecastMetrics>((resolve, reject) => {
            lastValueFrom(this.http.post<SiteActivationForecastMetrics>(this.Core_URL + 'Portal/' + sponsorName + '/' + portalId + '/SiteActivationForecastMetrics', countries))
                .then((response?: SiteActivationForecastMetrics) => {
                    resolve(response!);
                },
                    (err) => {
                        reject(err);
                    });
        });
    }
    getSiteBySiteID(sponsorName: string, siteId: number) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.Core_URL + 'Portal/Site/' + sponsorName + '/' + siteId))
        .then((response?: any) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
          });
    });
  }
  getStudyPortalCreationDate(sponsorName: string, portalId: number) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.Core_URL + 'Portal/StudyPortalCreationDate/' + sponsorName + '/' + portalId))
        .then((response?: any) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
          });
    });
  }
    insertRaspberryAIMessage(sponsorName: string, portalId: number, aiMessage: AIMessage) {
      return new Promise<boolean>((resolve, reject) => {
          lastValueFrom(this.http.post<boolean>(this.Core_URL + 'Portal/' + sponsorName + '/' + portalId + '/AIMessage', aiMessage))
              .then((response?: boolean) => {
                  resolve(response!);
              },
                  (err) => {
                      reject(err);
                  });
      });
  }
  getAIPredefinedQuestions(sponsorName: string, portalId: number): Promise<AIPredefinedQuestion> {
    const cacheKey = this.AIPredefinedQuestionsLookupKey + '_' + portalId;
    return new Promise<AIPredefinedQuestion>((resolve) => {
      // Fast path: cache hit
      if (this.lookups.has(cacheKey)) {
        resolve(this.lookups.get(cacheKey));
        return;
      }
      // Fetch from API
      lastValueFrom(
        this.http.get<AIPredefinedQuestion>(
          this.Core_URL + 'Portal/' + sponsorName + '/' + portalId + '/AIPredefinedQuestions'
        )
      ).then(
        (questions?: AIPredefinedQuestion) => {
          const result = questions ?? new AIPredefinedQuestion();
          if (questions) {
            this.lookups.set(cacheKey, result);
          }
          resolve(result);
        },
        (err) => {
          // Match C#: swallow errors and return an empty object so the UI still renders
          console.error(`getAIPredefinedQuestions failed for ${portalId}:`, err);
          resolve(new AIPredefinedQuestion());
        }
      );
    });
  }
  getAIRootQuestionMetrics(sponsorName: string, portalId: number, startDate: Date, endDate: Date) {
    return new Promise<RootQuestionMetric[]>((resolve, reject) => {
       const startDateTime = encodeURIComponent(this.formatLocalDateTime(startDate));
       const endDateTime = encodeURIComponent(this.formatLocalDateTime(endDate));

      lastValueFrom(this.http.get<RootQuestionMetric[]>(this.Core_URL + 'Portal/AIRootQuestionMetrics/' + sponsorName + '/' + portalId + '/' + startDateTime + '/' + endDateTime))
        .then((response?: RootQuestionMetric[]) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
          });
    });
  }

  getUserAdminFeatureNavigation(sponsorName: string, portalId: number): Promise<IPortalNavigation[]> {
    return new Promise<IPortalNavigation[]>((resolve, reject) => {
      if (this.lookups.has(this.UserAdminFeatureNavigationLookupKey + '_' + portalId)) {
        resolve(this.lookups.get(this.UserAdminFeatureNavigationLookupKey + '_' + portalId));
      } else {
        this.http.get<IPortalNavigation[]>(this.Core_URL + `User/AdminUserFeatures/${sponsorName}/${portalId}`)
          .toPromise()
          .then((navigations) => {
            this.lookups.set(this.UserAdminFeatureNavigationLookupKey + '_' + portalId, navigations);
            resolve(navigations!);
          }, (err) => {
            reject(err);
          });
      }
    });
  }

  getMedPointAdminFeatureNavigation(sponsorName: string): Promise<IPortalNavigation[]> {
    return new Promise<IPortalNavigation[]>((resolve, reject) => {
      if (this.lookups.has(this.MedPointAdminFeatureNavigationLookupKey + '_' + sponsorName)) {
        resolve(this.lookups.get(this.MedPointAdminFeatureNavigationLookupKey + '_' + sponsorName));
      } else {
        this.http.get<IPortalNavigation[]>(this.Core_URL + `User/MedPointAdminUserFeatures/${sponsorName}`)
          .toPromise()
          .then((navigations) => {
            this.lookups.set(this.MedPointAdminFeatureNavigationLookupKey + '_' + sponsorName, navigations);
            resolve(navigations!);
          }, (err) => {
            reject(err);
          });
      }
    });
  }

  createUser(sponsorName: string, userModel: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.Core_URL + `User/standard/${sponsorName}`, userModel))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getUserDetailsByEmail(email: string): Promise<any> {
    const params = new HttpParams().set('email', email);
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(
        `${this.Core_URL}User/UserDetailsByEmail`,
        { params }
      )).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  getUserDetailsByUserId(userId: number): Promise<any> {    
    return new Promise<any>((resolve, reject) => {     
        lastValueFrom(this.http.get<any>(this.Core_URL + `User/UserDetailsByUserId/${userId}`, {}
      )).then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getPortalRoles(sponsorName: string, portalId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.Core_URL + `Portal/Roles/${sponsorName}/${portalId}`))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getSitesByRole(sponsorName: string, portalId: number, roleId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.Core_URL + `Portal/RoleSites/${sponsorName}/${portalId}/${roleId}`))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getFeatureAuthorizedPortals(sponsorName: string, featureName: string): Promise<number[]> {
    return new Promise<number[]>((resolve, reject) => {
      lastValueFrom(this.http.get<number[]>(this.Core_URL + `User/UserFeaturePortals/${sponsorName}/${featureName}`))
        .then(
          (response?: number[]) => {
            resolve(response || []);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getUserActivePortals(sponsorName: string, userId: number): Promise<number[]> {
    return new Promise<number[]>((resolve, reject) => {
      lastValueFrom(this.http.get<number[]>(this.Core_URL + `User/UserActivePortals/${sponsorName}/${userId}`))
        .then(
          (response?: number[]) => {
            resolve(response || []);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  searchUsers(sponsorName: string, portalId: number, searchText: string): Promise<any[]> {
    const params = new HttpParams().set('searchText', searchText);
    return new Promise<any[]>((resolve, reject) => {
      lastValueFrom(this.http.get<any[]>(
        `${this.Core_URL}User/SearchUsers/${sponsorName}/${portalId}`,
        { params }
      )).then(
          (response?: any[]) => {
            resolve(response || []);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getUserPortalNodes(sponsorName: string, userId: number, portalId: number): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      lastValueFrom(this.http.get<any[]>(this.Core_URL + `User/UserPortalNodes/${sponsorName}/${userId}/${portalId}`))
        .then(
          (response?: any[]) => {
            resolve(response || []);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  updateUser(sponsorName: string, model: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.put<any>(this.Core_URL + `User/${sponsorName}`, model))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  resetPassword(sponsorName: string, userId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.Core_URL + `User/ResetPassword/${sponsorName}/${userId}`, {}))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  resendInvitation(sponsorName: string, model: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.Core_URL + `User/ResendInvitation/${sponsorName}`, model))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  importUsers(sponsorName: string, portalId: string, importUsersData: any[], isStartupEnabled: boolean): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.Core_URL + `User/ImportUsers/${sponsorName}`, {
        importUsersData,
        portalId,
        sponsorName,
        isStartupEnabled
      }))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }
  getAIFeedbackMetrics(sponsorName: string, portalId: number, startDate: Date, endDate: Date) {
    return new Promise<AIFeedbackMetric[]>((resolve, reject) => {
      const startDateTime = encodeURIComponent(this.formatLocalDateTime(startDate));
      const endDateTime = encodeURIComponent(this.formatLocalDateTime(endDate));

      lastValueFrom(this.http.get<AIFeedbackMetric[]>(this.Core_URL + 'Portal/AIFeedbackMetrics/' + sponsorName + '/' + portalId + '/' + startDateTime + '/' + endDateTime))
        .then((response?: AIFeedbackMetric[]) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
          });
    });
  }
  getAIQuestionCountMetrics(sponsorName: string, portalId: number, startDate: Date, endDate: Date) {
    return new Promise<AIQuestionCountMetric[]>((resolve, reject) => {
       const startDateTime = encodeURIComponent(this.formatLocalDateTime(startDate));
       const endDateTime = encodeURIComponent(this.formatLocalDateTime(endDate));

      lastValueFrom(this.http.get<AIQuestionCountMetric[]>(this.Core_URL + 'Portal/AIQuestionCountMetrics/' + sponsorName + '/' + portalId + '/' + startDateTime + '/' + endDateTime))
        .then((response?: AIQuestionCountMetric[]) => {
          resolve(response!);
        },
          (err) => {
            reject(err);
          });
    });
  }
  managePersonPortalRecentVisit(
    sponsorName:string, portalId: number, userId: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.post<any>(
          this.Core_URL + 'User/' + sponsorName + '/' + portalId + '/' + userId +'/PersonPortalRecentVisit',
          {}
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getRecentlyVisitedPortals(
     userId: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.get<any>(
          this.Core_URL + 'User/' + Helpers.getPlatformId() + '/' + userId +'/RecentlyVisitedPortals'
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getPortalHomeMyTasks(
    sponsorName:string, portalId: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.get<any>(
          this.Core_URL + 'Portal/' + sponsorName + '/' + portalId +'/RAIPortalHomeMyTasks'
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  getPortalHomeSearch(
    sponsorName:string, portalId: number, searchQueryRequest:SearchQueryRequest
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(
        this.http.post<any>(
          this.Core_URL + 'Portal/' + sponsorName + '/' + portalId +'/RAIHomeSearch',
          searchQueryRequest
        )
      ).then(
        (response?: any) => {
          resolve(response);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
}
